import { Heart, ShoppingCart } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useCart } from '../CartContext';
import { toast } from 'sonner';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://kiyafet-magazasi-backend.vercel.app';

interface ProductCardProps {
  id: number;
  name: string;
  price: string;
  oldPrice?: string;
  image: string;
  image2?: string;
  badge?: string;
  outOfStock?: boolean;
}

export function ProductCard({ id, name, price, oldPrice, image, image2, badge, outOfStock }: ProductCardProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { addToCart } = useCart();

  useEffect(() => {
    const kullanici = JSON.parse(localStorage.getItem('kullanici') || 'null');
    if (kullanici) {
      fetch(`${API_URL}/api/favorilerim/${kullanici.id}`)
        .then(r => r.json())
        .then(favs => {
          if (Array.isArray(favs) && favs.some((f: any) => f.id === id || f.urunId === id)) {
            setIsLiked(true);
          }
        }).catch(() => {});
    }
  }, [id]);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (outOfStock) { toast.error('Bu ürün stokta yok'); return; }
    // "1.250 TL" veya "1250.00 TL" formatlarını doğru parse et
    const numericPrice = parseFloat(price.replace(/[^\d,]/g, '').replace(',', '.')) || 
                         parseFloat(price.replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '')) || 0;
    addToCart({ id, name, price: numericPrice, image });
  };

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const kullanici = JSON.parse(localStorage.getItem('kullanici') || 'null');
    if (!kullanici) {
      toast.error('Favori eklemek için giriş yapın');
      window.location.href = '/hesabim';
      return;
    }
    try {
      if (isLiked) {
        await fetch(`${API_URL}/api/favori-cikar/${kullanici.id}/${id}`, { method: 'DELETE' });
        setIsLiked(false);
        toast.success('Favorilerden çıkarıldı');
      } else {
        await fetch(`${API_URL}/api/favori-ekle`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ kullaniciId: kullanici.id, urunId: id })
        });
        setIsLiked(true);
        toast.success('Favorilere eklendi!');
      }
    } catch {
      toast.error('Bir hata oluştu');
    }
  };

  return (
    <a 
      href={`/urun/${id}`}
      className="group relative bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow block"
    >
      <div 
        className="relative aspect-[3/4] overflow-hidden bg-gray-100"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img 
          src={isHovered && image2 ? image2 : image} 
          alt={name} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
        />
        
        {outOfStock && (
          <div className="absolute top-3 left-3 bg-gray-900 text-white px-3 py-1 rounded-full text-xs font-bold">Tükendi</div>
        )}
        {!outOfStock && badge && (
          <div className="absolute top-3 left-3 bg-pink-600 text-white px-3 py-1 rounded-full text-xs font-bold">{badge}</div>
        )}
        
        <button 
          onClick={handleLike}
          className="absolute top-3 right-3 bg-white size-9 rounded-full flex items-center justify-center hover:bg-pink-50 transition-colors shadow-md"
        >
          <Heart className={`size-4 ${isLiked ? 'fill-pink-600 text-pink-600' : 'text-gray-700'}`} />
        </button>

        {!outOfStock && (
          <button 
            onClick={handleAddToCart}
            className="absolute bottom-0 left-0 right-0 bg-pink-600 text-white py-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 font-semibold"
          >
            <ShoppingCart className="size-4" />
            <span>Sepete Ekle</span>
          </button>
        )}
      </div>

      <div className="p-3">
        <h3 className="font-medium text-gray-900 mb-2 text-sm line-clamp-2">{name}</h3>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-pink-600">{price}</span>
          {oldPrice && <span className="text-sm text-gray-400 line-through">{oldPrice}</span>}
        </div>
      </div>
    </a>
  );
}